/** Fixed window name — reuses/focuses one call tab instead of opening many. */
export const LIVEKIT_CALL_WINDOW_NAME = 'workloop-livekit-call';

export const LIVEKIT_CALL_PATH = '/call';

const usesHashRouter = () => {
  if (typeof window === 'undefined') return false;
  if (window.workloopRealm) return true;
  return (window.location.hash || '').startsWith('#/');
};

const routePathFromLocation = () => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash || '';
  if (hash.startsWith('#/')) {
    return hash.slice(1).split('?')[0] || '/';
  }
  return window.location.pathname || '';
};

export const isDesktopCallWindow = () => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.workloopRealm?.isCallWindow?.());
  } catch {
    return false;
  }
};

export const isLiveKitCallPath = (pathname) => {
  const path = pathname || routePathFromLocation();
  return Boolean(path && (path === LIVEKIT_CALL_PATH || path.startsWith(`${LIVEKIT_CALL_PATH}/`)));
};

export const buildLiveKitCallUrl = ({ channelId, messageId, roomName, title }) => {
  const params = new URLSearchParams();
  params.set('channelId', channelId);
  if (messageId) params.set('messageId', messageId);
  if (roomName) params.set('roomName', roomName);
  if (title) params.set('title', title);
  const query = params.toString();
  const pathWithQuery = `${LIVEKIT_CALL_PATH}?${query}`;

  if (typeof window === 'undefined') return pathWithQuery;

  if (usesHashRouter()) {
    const origin = window.location.origin || '';
    const filePath = window.location.pathname || '/';
    const search = window.location.search || '';
    return `${origin}${filePath}${search}#${pathWithQuery}`;
  }

  return `${window.location.origin}${pathWithQuery}`;
};

/**
 * Open (or focus) the Direct Call surface.
 * Browser: one named tab. Desktop: a dedicated Electron window.
 */
export const openLiveKitCallWindow = ({ channelId, messageId, roomName, title }) => {
  if (!channelId) return null;

  const url = buildLiveKitCallUrl({ channelId, messageId, roomName, title });
  const callTitle = title || 'Direct call';

  if (typeof window !== 'undefined' && window.workloopRealm?.openCallWindow) {
    try {
      const result = window.workloopRealm.openCallWindow({
        channelId,
        messageId,
        roomName,
        title: callTitle,
        url,
      });
      if (result && typeof result.then === 'function') {
        result.catch(() => {});
      }
      return { ok: true, desktop: true };
    } catch {
      // Fall through to window.open
    }
  }

  let win = null;
  try {
    win = window.open('', LIVEKIT_CALL_WINDOW_NAME);
  } catch {
    win = null;
  }

  if (win && !win.closed) {
    try {
      const sameOrigin = win.location.origin === window.location.origin;
      const onCallPage = sameOrigin && isLiveKitCallPath(
        usesHashRouter()
          ? (win.location.hash || '').replace(/^#/, '').split('?')[0]
          : win.location.pathname
      );

      if (onCallPage && messageId) {
        const href = win.location.href;
        const query = usesHashRouter()
          ? (win.location.hash.split('?')[1] || '')
          : (win.location.search || '').replace(/^\?/, '');
        const currentMsg = new URLSearchParams(query).get('messageId');
        if (currentMsg === String(messageId) && href) {
          win.focus();
          return win;
        }
      }

      win.location.href = url;
      win.focus();
      return win;
    } catch {
      // Cross-origin blank or inaccessible — open fresh below
    }
  }

  win = window.open(url, LIVEKIT_CALL_WINDOW_NAME);
  if (win) {
    try {
      win.focus();
    } catch {
      // ignore
    }
  }
  return win;
};
